import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { GetUser, UpdateUser } from "../../../../Redux/Slices/User";
import { yearOption, monthOption } from "../../../../Helper/Option";
import ProfessionalQualification from "./ProfessionalQualification";
import AcademicDetails from "./AcademicDetails";
import Experience from "./Experience";
import AdditionalDetails from "./AdditionalDetails";
import CareerProfile from "./CareerProfile";
import Resume from "./Resume";
import Location from "../../../Admin/Location";
import { HiOutlinePencilSquare } from "react-icons/hi2";
import { FaArrowLeft } from "react-icons/fa";
import "./JobseekerProfileDashboard.css";
import "./ProfileDetailsModern.css";

const ProfileDetails = () => {
  const id = localStorage.getItem("id");
  const { userData, completionPercentage, loading } = useSelector((state) => state?.User);
  const [editData, setEditData] = useState({});
  const [error, setError] = useState({});
  const [editingPersonal, setEditingPersonal] = useState(false);
  const [locationData, setLocationData] = useState({
    Country: "",
    State: "",
    Cities: "",
  });
  const dispatch = useDispatch();
  const pct = Number(completionPercentage) || 0;

  useEffect(() => {
    dispatch(GetUser(id));
    document.title = "Edit Profile | CAJobPortal";
  }, [dispatch, id]);

  useEffect(() => {
    if (userData) setEditData(userData);
  }, [userData]);

  const validation = () => {
    const errs = {};
    if (!editData.name) errs.name = "Name is required";
    if (!editData.mobile) errs.mobile = "Mobile is required";
    return errs;
  };

  const postUserData = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
    setError((prev) => ({ ...prev, [name]: "" }));
  };

  const handleUpdatePersonal = async () => {
    const ErrorList = validation();
    setError(ErrorList);
    if (Object.keys(ErrorList).length) return;

    const years = parseFloat(editData?.year) || 0;
    const months = parseFloat(editData?.month) || 0;
    const totalMonths = years * 12 + months;
    const remainingMonths = totalMonths % 12;
    const formattedMonths =
      remainingMonths < 10 ? `0${remainingMonths}` : remainingMonths;
    const totalExperience = `${Math.floor(totalMonths / 12)}.${formattedMonths}`;

    const updateData = {
      name: editData?.name,
      mobile: editData?.mobile,
      total_experience: totalExperience || editData?.total_experience,
      experience_level: editData?.experience_level,
      address: {
        country: locationData?.Country || editData?.address?.country,
        state: locationData?.State || editData?.address?.state,
        city: Array.isArray(locationData?.Cities)
          ? locationData.Cities[0]
          : locationData?.Cities || editData?.address?.city,
      },
    };

    await UpdateUser(id, updateData, "application/json");
    dispatch(GetUser(id));
    setEditingPersonal(false);
  };

  const scrollTo = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="jsp-page">
      <div className="jsp-shell profile-details-shell">
        <header className="jsp-intro profile-details-header">
          <Link to="/jobseeker/profile" className="profile-details-back">
            <FaArrowLeft /> Back to profile
          </Link>
          <p className="jsp-greeting">Complete your profile</p>
          <h1 className="jsp-title">Profile details</h1>
          <p className="jsp-subtitle">
            Keep your information up to date so employers can shortlist you faster.
          </p>
          <div className="profile-details-completion">
            <div className="profile-details-completion-bar">
              <span style={{ width: `${pct}%` }} />
            </div>
            <strong>{pct}% complete</strong>
          </div>
        </header>

        <nav className="profile-details-nav" aria-label="Profile sections">
          {[
            ["resume", "Resume"],
            ["personal", "Personal"],
            ["experience", "Experience"],
            ["education", "Education"],
            ["career", "Career"],
            ["additional", "More"],
          ].map(([idKey, label]) => (
            <button key={idKey} type="button" onClick={() => scrollTo(idKey)}>
              {label}
            </button>
          ))}
        </nav>

        <div id="resume" className="profile-details-section">
          <Resume userData={userData} />
        </div>

        <section id="personal" className="jsp-card profile-details-section">
          <div className="jsp-card-head">
            <div className="jsp-card-head-left">
              <div>
                <h3 className="jsp-card-title">Personal info</h3>
                <p className="jsp-card-desc">Basic details visible to recruiters</p>
              </div>
            </div>
            <button
              type="button"
              className="jsp-btn jsp-btn-secondary"
              onClick={() => setEditingPersonal((v) => !v)}
            >
              <HiOutlinePencilSquare /> {editingPersonal ? "Cancel" : "Edit"}
            </button>
          </div>

          {!editingPersonal ? (
            <div className="profile-details-grid">
              <div><span>Name</span><strong>{userData?.name || "—"}</strong></div>
              <div><span>Email</span><strong>{userData?.email || "—"}</strong></div>
              <div><span>Phone</span><strong>{userData?.mobile || "—"}</strong></div>
              <div><span>Experience</span><strong>{userData?.experience_level || "—"}</strong></div>
              <div className="profile-details-span-2">
                <span>Location</span>
                <strong>
                  {[userData?.address?.city, userData?.address?.state, userData?.address?.country]
                    .filter(Boolean)
                    .join(", ") || "—"}
                </strong>
              </div>
            </div>
          ) : (
            <div className="profile-details-form">
              <label>
                Name
                <input name="name" value={editData?.name || ""} onChange={postUserData} />
                {error.name && <em>{error.name}</em>}
              </label>
              <label>
                Mobile
                <input name="mobile" value={editData?.mobile || ""} onChange={postUserData} />
                {error.mobile && <em>{error.mobile}</em>}
              </label>
              <label>
                Experience level
                <select
                  name="experience_level"
                  value={editData?.experience_level || ""}
                  onChange={postUserData}
                >
                  <option value="">Select</option>
                  <option value="Fresher">Fresher</option>
                  <option value="Experienced">Experienced</option>
                </select>
              </label>
              <div className="profile-details-exp-row">
                <select name="year" onChange={postUserData} className="form-select">
                  {yearOption?.map((value) => (
                    <option key={value} value={value}>{value} Year</option>
                  ))}
                </select>
                <select name="month" onChange={postUserData} className="form-select">
                  {monthOption?.map((value) => (
                    <option key={value} value={value}>{value} Month</option>
                  ))}
                </select>
              </div>
              <Location sendDataToParent={setLocationData} />
              <button type="button" className="jsp-btn jsp-btn-primary" onClick={handleUpdatePersonal}>
                Save personal info
              </button>
            </div>
          )}
        </section>

        <div id="experience" className="profile-details-section">
          <Experience userData={userData} />
        </div>

        <div id="education" className="profile-details-section">
          <ProfessionalQualification userData={userData} />
          <AcademicDetails userData={userData} />
        </div>

        <div id="career" className="profile-details-section">
          <CareerProfile userData={userData} />
        </div>

        <div id="additional" className="profile-details-section">
          <AdditionalDetails userData={userData} />
        </div>

        {loading && !userData && (
          <p className="profile-details-loading">Loading profile...</p>
        )}
      </div>
    </div>
  );
};

export default ProfileDetails;
